import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useContactList } from "@/modules/contacts/hooks/useContacts";
import { useProposalList } from "@/modules/costing/hooks/useProposals";
import { useExpenseList } from "@/modules/expenses/hooks/useExpenses";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useMeetingList } from "@/modules/meetings/hooks/useMeetings";
import { NewContactDrawer } from "@/modules/contacts/components/NewContactDrawer";
import { NewProposalDrawer } from "@/modules/costing/components/NewProposalDrawer";
import { NewExpenseDrawer } from "@/modules/expenses/components/NewExpenseDrawer";
import { NewProjectDrawer } from "@/modules/projects/components/NewProjectDrawer";
import { formatDate } from "@/lib/utils";

const CARD_BORDER = "hsl(210,30%,85%)";
const TABLE_HEAD_BG = "hsl(210,30%,97%)";

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center rounded-xl border bg-card py-16 text-sm text-muted-foreground" style={{ borderColor: CARD_BORDER }}>
      {message}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center rounded-xl border bg-card py-16 text-sm text-muted-foreground" style={{ borderColor: CARD_BORDER }}>
      <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
      Loading…
    </div>
  );
}

// ── Contacts ──────────────────────────────────────────────────

export function ExternalContactsTab({ companyId }: { companyId: string }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const { data, isLoading } = useContactList({ companyId, limit: 100 });
  const contacts = data?.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setDrawerOpen(true)}>
          <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
          Add Contact
        </Button>
      </div>
      {isLoading ? (
        <LoadingState />
      ) : contacts.length === 0 ? (
        <EmptyState message="No contacts linked to this company yet." />
      ) : (
        <div
          className="overflow-hidden rounded-xl border bg-card"
          style={{ borderColor: CARD_BORDER }}
        >
          <table className="w-full text-sm">
            <thead className="text-left text-xs font-medium uppercase tracking-wide text-muted-foreground" style={{ background: TABLE_HEAD_BG }}>
              <tr>
                <th className="px-6 py-3">Name + Code</th>
                <th className="px-6 py-3">Job Title</th>
                <th className="px-6 py-3">Email</th>
                <th className="px-6 py-3">Phone</th>
                <th className="px-6 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {contacts.map((c) => (
                <tr
                  key={c.id}
                  className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
                  onClick={() => navigate(`/contacts/${c.id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="font-medium text-foreground">{c.fullName}</div>
                    <div className="mt-0.5 font-mono text-xs text-muted-foreground">{c.code}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{c.jobTitle ?? "—"}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{c.email ?? "—"}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{c.phone ?? "—"}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{c.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <NewContactDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        defaultCompanyId={companyId}
      />
    </div>
  );
}

// ── Proposals ─────────────────────────────────────────────────

export function ExternalProposalsTab({ companyId }: { companyId: string }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const { data, isLoading } = useProposalList({
    recipientCompanyId: companyId,
    limit: 100,
  });
  const proposals = data?.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setDrawerOpen(true)}>
          <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
          Add Proposal
        </Button>
      </div>
      {isLoading ? (
        <LoadingState />
      ) : proposals.length === 0 ? (
        <EmptyState message="No proposals addressed to this company yet." />
      ) : (
        <div
          className="overflow-hidden rounded-xl border bg-card"
          style={{ borderColor: CARD_BORDER }}
        >
          <table className="w-full text-sm">
            <thead className="text-left text-xs font-medium uppercase tracking-wide text-muted-foreground" style={{ background: TABLE_HEAD_BG }}>
              <tr>
                <th className="px-6 py-3">Name + Code</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Total</th>
                <th className="px-6 py-3">Valid Until</th>
                <th className="px-6 py-3">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {proposals.map((p) => (
                <tr
                  key={p.id}
                  className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
                  onClick={() => navigate(`/proposals/${p.id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="font-medium text-foreground">{p.name}</div>
                    <div className="mt-0.5 font-mono text-xs text-muted-foreground">{p.code}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{p.status}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {p.currency} {p.total.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {p.validUntil ? formatDate(p.validUntil) : "—"}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {formatDate(p.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <NewProposalDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        defaultRecipientCompanyId={companyId}
      />
    </div>
  );
}

// ── Projects (external client) ────────────────────────────────

export function ExternalProjectsTab({ companyId }: { companyId: string }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const { data, isLoading } = useProjectList({
    clientCompanyId: companyId,
    limit: 100,
  });
  const projects = data?.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setDrawerOpen(true)}>
          <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
          Add Project
        </Button>
      </div>
      {isLoading ? (
        <LoadingState />
      ) : projects.length === 0 ? (
        <EmptyState message="No projects linked to this client yet." />
      ) : (
        <div
          className="overflow-hidden rounded-xl border bg-card"
          style={{ borderColor: CARD_BORDER }}
        >
          <table className="w-full text-sm">
            <thead className="text-left text-xs font-medium uppercase tracking-wide text-muted-foreground" style={{ background: TABLE_HEAD_BG }}>
              <tr>
                <th className="px-6 py-3">Name + Code</th>
                <th className="px-6 py-3">Type</th>
                <th className="px-6 py-3">Product</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Progress</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {projects.map((p) => (
                <tr
                  key={p.id}
                  className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
                  onClick={() => navigate(`/projects/${p.id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="font-medium text-foreground">{p.name}</div>
                    <div className="mt-0.5 font-mono text-xs text-muted-foreground">{p.code}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{p.type}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{p.productName ?? "—"}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{p.status}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-16 rounded-full bg-[hsl(210,16%,93%)]">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${p.progress ?? 0}%` }}
                        />
                      </div>
                      <span className="text-xs">{p.progress ?? 0}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <NewProjectDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        defaultClientCompanyId={companyId}
      />
    </div>
  );
}

// ── Meetings (view-only — External Companies are a reference module) ──

export function ExternalMeetingsTab({ companyId }: { companyId: string }) {
  const navigate = useNavigate();
  const { data, isLoading } = useMeetingList({
    attendeeCompanyId: companyId,
    limit: 100,
  });
  const meetings = data?.data ?? [];

  return (
    <div className="space-y-3">
      {isLoading ? (
        <LoadingState />
      ) : meetings.length === 0 ? (
        <EmptyState message="No meetings linked to this company's contacts yet." />
      ) : (
        <div
          className="overflow-hidden rounded-xl border bg-card"
          style={{ borderColor: CARD_BORDER }}
        >
          <table className="w-full text-sm">
            <thead className="text-left text-xs font-medium uppercase tracking-wide text-muted-foreground" style={{ background: TABLE_HEAD_BG }}>
              <tr>
                <th className="px-6 py-3">Title + Code</th>
                <th className="px-6 py-3">Type</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Scheduled</th>
                <th className="px-6 py-3">Organizer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {meetings.map((m) => (
                <tr
                  key={m.id}
                  className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
                  onClick={() => navigate(`/meetings/${m.id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="font-medium text-foreground">{m.title}</div>
                    <div className="mt-0.5 font-mono text-xs text-muted-foreground">{m.code}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{m.type}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{m.status}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{formatDate(m.scheduledAt)}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{m.organizerName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── External Expenses (vendor) ────────────────────────────────

export function ExternalExpensesTab({ companyId }: { companyId: string }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const { data, isLoading } = useExpenseList({
    vendorCompanyId: companyId,
    limit: 100,
  });
  const expenses = data?.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setDrawerOpen(true)}>
          <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
          Add Expense
        </Button>
      </div>
      {isLoading ? (
        <LoadingState />
      ) : expenses.length === 0 ? (
        <EmptyState message="No expenses paid to this vendor yet." />
      ) : (
        <div
          className="overflow-hidden rounded-xl border bg-card"
          style={{ borderColor: CARD_BORDER }}
        >
          <table className="w-full text-sm">
            <thead className="text-left text-xs font-medium uppercase tracking-wide text-muted-foreground" style={{ background: TABLE_HEAD_BG }}>
              <tr>
                <th className="px-6 py-3">Title + Code</th>
                <th className="px-6 py-3">Amount</th>
                <th className="px-6 py-3">Category</th>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {expenses.map((e) => (
                <tr
                  key={e.id}
                  className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
                  onClick={() => navigate(`/expenses/${e.id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="font-medium text-foreground">{e.title}</div>
                    <div className="mt-0.5 font-mono text-xs text-muted-foreground">{e.code}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {e.currency} {e.amount.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{e.category ?? "—"}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{formatDate(e.expenseDate)}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{e.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <NewExpenseDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        defaultVendorCompanyId={companyId}
      />
    </div>
  );
}
